import { memo } from 'react';
import {
  type DragEndEvent,
  KanbanBoard,
  KanbanCards,
  KanbanHeader,
  KanbanProvider,
} from '@/components/ui/shadcn-io/kanban';
import { TaskCard } from './TaskCard';
import type { TaskWithAttemptStatus } from 'shared/types';
import { resolveColumnColor } from '@/constants/columnColors';

export type KanbanColumn = {
  id: string;
  name: string;
  color: string;
};

export type KanbanColumnsMap = Record<string, TaskWithAttemptStatus[]>;

interface TaskKanbanBoardProps {
  columns: KanbanColumn[];
  tasksByColumn: KanbanColumnsMap;
  onDragEnd: (event: DragEndEvent) => void;
  onViewTaskDetails: (task: TaskWithAttemptStatus) => void;
  selectedTaskId?: string;
  onCreateTask?: (columnId?: string) => void;
  onRunSelectedTasks?: () => void;
  projectId: string;
  showRunSelectedTasks?: boolean;
  todoColumnId?: string;
}

function TaskKanbanBoard({
  columns,
  tasksByColumn,
  onDragEnd,
  onViewTaskDetails,
  selectedTaskId,
  onCreateTask,
  onRunSelectedTasks,
  projectId,
  showRunSelectedTasks = false,
  todoColumnId,
}: TaskKanbanBoardProps) {
  return (
    <KanbanProvider onDragEnd={onDragEnd}>
      {columns.map((column) => {
        const tasks = tasksByColumn[column.id] || [];
        const isTodoColumn = column.id === todoColumnId;
        const hasTodoTasks = isTodoColumn && tasks.length > 0;
        const resolvedColor = resolveColumnColor(column.color);

        return (
          <KanbanBoard key={column.id} id={column.id}>
            <KanbanHeader
              name={column.name}
              color={resolvedColor}
              onAddTask={() => onCreateTask?.(column.id)}
              onRunSelectedTasks={onRunSelectedTasks}
              showRunSelectedTasks={hasTodoTasks && showRunSelectedTasks}
            />
            <KanbanCards>
              {tasks.map((task, index) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  index={index}
                  status={task.status}
                  onViewDetails={onViewTaskDetails}
                  isOpen={selectedTaskId === task.id}
                  projectId={projectId}
                />
              ))}
            </KanbanCards>
          </KanbanBoard>
        );
      })}
    </KanbanProvider>
  );
}

export default memo(TaskKanbanBoard);
